import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pin, PinOff, Tag, AlertTriangle, Clock, Cpu, DollarSign, RefreshCw, FileCheck } from 'lucide-react';
import { Button, Input, Select, Spinner } from '../../components/ui';
import { StepOutputViewer, isAgentStepOutput } from '../../components/agents/output/StepOutputViewer.js';
import { useSnapshots, useUpdateSnapshot, usePurgeSnapshots } from '../../hooks/useSnapshots.js';
import { computeContractHash } from '../../utils/contract-utils.js';
import type { Snapshot } from '../../generated/api/types.gen.js';
import { listWorkflowExecutionObservationsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { ExecutionObservationSection } from './StepDebugObservation.js';
import { ApprovalSection } from './StepDebugApprovalSection.js';
import { CleanupSection } from './StepDebugCleanupSection.js';
import { formatDate, inferJsonSchema, snapshotOutputObject } from './stepDebugOutput.utils.js';
/** Contract shape for debug output panel (matches contract-utils.StepContract) */
interface StepContractLike {
  outputContract?: unknown;
  inputContracts?: unknown;
  negotiationStatus?: string;
  version?: number;
}

interface StepDebugOutputPanelProps {
  templateId: number;
  stepId: string;
  contract?: StepContractLike;
  onRerun?: () => void;
  stepApproval?: {
    status: 'pending' | 'approved' | 'rejected';
    approvedAt?: string;
    snapshot_id?: number;
    notes?: string;
  };
  onApprove?: (snapshotId: number) => void;
  onReject?: (notes: string) => void;
  onPinAsContract?: (contract: {
    jsonSchema: Record<string, unknown>;
    strict: boolean;
    example?: Record<string, unknown>;
  }) => void;
}

export const StepDebugOutputPanel: React.FC<StepDebugOutputPanelProps> = ({
  templateId,
  stepId,
  contract,
  onRerun,
  stepApproval,
  onApprove,
  onReject,
  onPinAsContract,
}) => {
  const { data: snapshots, isLoading, isError } = useSnapshots(templateId, stepId);
  const { data: executionObservations } = useQuery({
    ...listWorkflowExecutionObservationsOptions({ path: { id: String(templateId) }, query: { stepId, limit: 30 } }),
    enabled: Boolean(templateId && stepId),
  });
  const updateMutation = useUpdateSnapshot();
  const purgeMutation = usePurgeSnapshots();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [currentContractHash, setCurrentContractHash] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<number | null>(null);
  const [labelText, setLabelText] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  // Compute current contract hash
  useEffect(() => {
    const arg = contract?.outputContract
      ? {
          outputContract: contract.outputContract,
          negotiationStatus: contract?.negotiationStatus,
          version: contract?.version,
        }
      : undefined;
    computeContractHash(arg).then(setCurrentContractHash);
  }, [contract]);

  const items = snapshots?.data ?? [];
  const selected = selectedId ? items.find((s) => s.id === selectedId) : items[0];
  const runObservation = Array.isArray(executionObservations?.steps) ? executionObservations.steps[0] : null;

  // Auto-select latest when new snapshots load
  useEffect(() => {
    if (items.length > 0 && !selectedId) {
      setSelectedId(items[0].id);
    }
  }, [items.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-4 text-xs text-muted" role="status">
        <Spinner size="sm" /> Loading snapshots...
      </div>
    );
  }

  if (isError) {
    return <div className="p-4 text-center text-danger text-xs">Could not load debug snapshots.</div>;
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3 border-t">
        {runObservation && (
          <ExecutionObservationSection observation={runObservation} onPinAsContract={onPinAsContract} />
        )}
        <div className="p-4 text-center text-muted text-xs">
          No debug outputs yet. Run the step above to generate output.
        </div>
      </div>
    );
  }

  const hasDrift = selected?.contractHash && currentContractHash && selected.contractHash !== currentContractHash;

  return (
    <div className="flex flex-col gap-3 p-3 border-t">
      {/* Snapshot selector */}
      <div className="flex items-center gap-2">
        <Select
          className="flex-1"
          value={selected?.id ?? ''}
          onChange={(e) => setSelectedId(Number(e.target.value))}
          aria-label="Select debug snapshot"
        >
          {items.map((s) => (
            <option key={s.id} value={s.id}>
              {formatDate(s.createdAt)}
              {s.label ? ` — ${s.label}` : ''}
              {s.pinned ? ' [pinned]' : ''}
            </option>
          ))}
        </Select>
        <span className="text-[10px] text-muted">
          {items.length} snapshot{items.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Contract drift warning */}
      {hasDrift && (
        <div className="flex items-center gap-2 bg-warning-muted text-warning rounded-sm px-3 py-2 text-xs">
          <AlertTriangle size={14} />
          <span className="flex-1">Contract changed since this output was produced. Output may be stale.</span>
          {onRerun && (
            <Button variant="ghost" size="sm" onClick={onRerun}>
              <RefreshCw size={12} /> Re-run
            </Button>
          )}
        </div>
      )}

      {runObservation && <ExecutionObservationSection observation={runObservation} onPinAsContract={onPinAsContract} />}

      {/* Metadata bar */}
      {selected && (
        <div className="flex items-center gap-3 text-[10px] text-muted flex-wrap">
          {selected.model && (
            <span className="flex items-center gap-1">
              <Cpu size={10} />
              {selected.model}
            </span>
          )}
          {selected.durationMs != null && (
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {(selected.durationMs / 1000).toFixed(1)}s
            </span>
          )}
          {selected.costUsd && (
            <span className="flex items-center gap-1">
              <DollarSign size={10} />${selected.costUsd}
            </span>
          )}

          {/* Pin toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => updateMutation.mutate({ id: selected.id, pinned: !selected.pinned })}
            title={selected.pinned ? 'Unpin' : 'Pin (exempt from cleanup)'}
          >
            {selected.pinned ? <PinOff size={10} /> : <Pin size={10} />}
            {selected.pinned ? 'Unpin' : 'Pin'}
          </Button>
          {onPinAsContract && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const output = snapshotOutputObject(selected);
                if (!output) return;
                onPinAsContract({
                  jsonSchema: inferJsonSchema(output),
                  strict: true,
                  example: output,
                });
              }}
              title="Use this debug output shape as the step output contract"
            >
              <FileCheck size={10} />
              Pin as contract
            </Button>
          )}

          {/* Label */}
          {editingLabel === selected.id ? (
            <div className="flex items-center gap-1">
              <Input
                className="w-28"
                aria-label="Snapshot label"
                value={labelText}
                onChange={(e) => setLabelText(e.target.value)}
                onBlur={() => setEditingLabel(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    updateMutation.mutate({ id: selected.id, label: labelText });
                    setEditingLabel(null);
                  } else if (e.key === 'Escape') {
                    setEditingLabel(null);
                  }
                }}
                placeholder="Label..."
              />
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingLabel(selected.id);
                setLabelText(selected.label ?? '');
              }}
            >
              <Tag size={10} />
              {selected.label || 'Add label'}
            </Button>
          )}
        </div>
      )}

      {/* Structured output viewer */}
      {selected && renderOutput(selected)}

      {/* Approval section */}
      {selected && (onApprove || onReject) && (
        <ApprovalSection
          approval={stepApproval}
          selectedSnapshot={selected}
          onApprove={onApprove}
          onReject={onReject}
          rejectNotes={rejectNotes}
          setRejectNotes={setRejectNotes}
          showRejectInput={showRejectInput}
          setShowRejectInput={setShowRejectInput}
        />
      )}

      {/* Data cleanup section */}
      <CleanupSection
        itemCount={items.length}
        templateId={templateId}
        purgeMutation={purgeMutation}
        showPurgeConfirm={showPurgeConfirm}
        setShowPurgeConfirm={setShowPurgeConfirm}
      />
    </div>
  );
};

function renderOutput(snapshot: Snapshot) {
  // Try outputJson first, then output
  const data = (snapshot.outputJson ?? snapshot.output) as Record<string, unknown> | null;
  if (!data || typeof data !== 'object') {
    // Fall back to raw text
    if (snapshot.fullText) {
      return (
        <pre className="text-xs text-body bg-surface rounded-sm p-3 overflow-auto max-h-[400px] whitespace-pre-wrap">
          {snapshot.fullText}
        </pre>
      );
    }
    return <div className="text-xs text-muted p-3">No output data</div>;
  }

  // Merge structured output parts captured by the snapshot row with any parts
  // already present in the agent response.
  const combined =
    snapshot.outputParts && Array.isArray(snapshot.outputParts)
      ? {
          ...data,
          outputParts: [...((data.outputParts as Record<string, unknown>[]) ?? []), ...snapshot.outputParts],
        }
      : data;

  if (isAgentStepOutput(combined)) {
    return <StepOutputViewer data={combined} />;
  }

  // Not a structured agent output — show with StepOutputViewer anyway (it has raw JSON fallback)
  return <StepOutputViewer data={combined} />;
}
